import axios from 'axios';
import { auth } from '../config/firebase';

const api = axios.create({
    baseURL: process.env.NODE_ENV === 'development' ? 'http://localhost:3000' : undefined,
    withCredentials: true,
    timeout: 30000 // 30 second timeout for all requests
});

const getCookie = (name) => {
    const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
    return match ? decodeURIComponent(match[1]) : null;
};

// Echo the double-submit CSRF cookie back as a header on state-changing requests
api.interceptors.request.use(config => {
    const method = (config.method || 'get').toUpperCase();
    if (!['GET', 'HEAD', 'OPTIONS'].includes(method)) {
        const csrfToken = getCookie('csrfToken');
        if (csrfToken) {
            config.headers['X-CSRF-Token'] = csrfToken;
        }
    }
    return config;
});

// Global token refresh state to prevent concurrent refreshes
let isRefreshing = false;
let failedQueue = []; // TODO: can potentially grow too large

const processQueue = (error, token = null) => {
    failedQueue.forEach(prom => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });
    
    failedQueue = [];
};

api.interceptors.response.use(
    response => response,
    async error => {
        const originalRequest = error.config;

        // Handle 401 errors with token refresh logic
        if (error.response && error.response.status === 401 && !originalRequest._retry && !originalRequest.skipAuthRefresh) {
            const data = error.response.data;

            if (data.code === 'token_expired') {
                if (isRefreshing) {
                    // If we're already refreshing, queue this request
                    return new Promise((resolve, reject) => {
                        failedQueue.push({ resolve, reject });
                        console.log('req added to failed queue, total: ' + failedQueue.length)
                    }).then(() => {
                        return api(originalRequest);
                    }).catch(err => {
                        return Promise.reject(err);
                    });
                }

                originalRequest._retry = true;
                isRefreshing = true;

                // Try to refresh the token
                const user = auth.currentUser;
                if (user) {
                    try {
                        const newToken = await user.getIdToken(true);

                        // Update the server-side cookie using the same API instance
                        await api.post('/api/auth/refresh-token', { idToken: newToken }, { skipAuthRefresh: true });

                        processQueue(null, newToken);
                        
                        // Retry the original request
                        return api(originalRequest);

                    } catch (refreshError) {
                        console.error('Token refresh failed:', refreshError);
                        processQueue(refreshError, null);
                        handleLogout();
                        return Promise.reject(error);
                    } finally {
                        isRefreshing = false;
                    }
                } else {
                    // No current user, redirect to login
                    isRefreshing = false;
                    processQueue(error, null);
                    handleLogout();
                    return Promise.reject(error);
                }
            } else {
                // Other auth errors, redirect to login
                handleLogout();
                return Promise.reject(error);
            }
        }

        // Handle other errors
        const errorResponse = {
            code: 'error',
            messages: []
        };

        if (error.response?.data?.messages) {
            errorResponse.code = error.response.data.code;
            errorResponse.messages = error.response.data.messages;
        } else if (error.response?.status === 404) {
            errorResponse.messages = ['Resurss nav atrasts.'];
        } else {
            errorResponse.messages = ['Notikusi neparedzēta kļūda.'];
        }

        return Promise.reject(errorResponse);
    }
);

const handleLogout = () => {
    // Sign out from Firebase
    if (auth.currentUser) {
        auth.signOut();
    }

    api.post('/api/auth/logout', {}, { timeout: 5000 });

    // Clear any localStorage items
    localStorage.removeItem('authToken');
    localStorage.removeItem('userData');

    // Redirect to login
    window.location.href = '/';
};

export default api;